import { LoadingSpinner } from "components/LoadingSpinner";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useDeleteContractHierarchyRuleMutation,
  useGetAllContractHierarchyRulesQuery,
} from "store/queries/contractHierarchyRules";
import {
  useDeleteExplodingFeeScheduleMutation,
  useGetAllExplodingFeeSchedulesQuery,
} from "store/queries/explodingFeeSchedule";
import {
  useDeleteInpatientRulesetMutation,
  useGetInpatientRulesetsQuery,
} from "store/queries/inpatientRulesets";
import {
  useDeleteLesserOfChargesMutation,
  useGetAllLesserOfChargesQuery,
} from "store/queries/lesserOfCharges";
import {
  useDeleteModifierAdjustmentMutation,
  useGetAllModifierAdjustmentsQuery,
} from "store/queries/modifierAdjustments";
import {
  useDeletePackageRulesetMutation,
  useGetAllPackageRulesetsQuery,
} from "store/queries/packageRuleset";
import {
  useDeleteCaseRateRulesetMutation,
  useGetCaseRateRulesetsQuery,
} from "store/queries/pricing";
import {
  useDeleteDiscountRulesetMutation,
  useGetAllDiscountRulesetsQuery,
} from "store/queries/procedureDiscountRuleset";
import {
  useDeleteProfessionalRulesetMutation,
  useGetAllProfessionalRulesetsQuery,
} from "store/queries/professionalRuleset";
import styled from "styled-components";
import type {
  IInpatientOutlierConfig,
  IPassThroughConfig,
  ITransferConfig,
  InpatientRulesetType,
} from "types/InpatientRuleset";

import { hydrateInpatientOutlierFormData } from "../../PayerRulesPage/helpers/inpatientOutlier";
import { defaultScopeCell } from "./defaultScopeCell";

const Row = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledDataTable = styled(DataTable)`
  table > thead > tr > th {
    padding: 1rem 0.5rem !important;
  }
  table > tbody > tr > td {
    padding: 1rem 0.5rem !important;
  }
`;

const ColumnWrapper = styled.div`
  display: block;
  max-height: 20px;
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
`;

const AdditionalInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const RuleTypeCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const RULE_TYPE_TAG_PALETTE: Record<
  string,
  { $bg: string; $fg: string; $border: string }
> = {
  MPPR: {
    $bg: "var(--primary-50)",
    $fg: "var(--primary-700)",
    $border: "var(--primary-300)",
  },
  "Case Rate": {
    $bg: "var(--primary-100)",
    $fg: "var(--primary-700)",
    $border: "var(--primary-200)",
  },
  Package: {
    $bg: "var(--primary-100)",
    $fg: "var(--primary-800)",
    $border: "var(--primary-200)",
  },
  "Exploding Fee Schedule": {
    $bg: "var(--primary-50)",
    $fg: "var(--primary-800)",
    $border: "var(--primary-300)",
  },
  Hierarchy: {
    $bg: "var(--primary-50)",
    $fg: "var(--primary-700)",
    $border: "var(--primary-300)",
  },
  Inpatient: {
    $bg: "var(--primary-100)",
    $fg: "var(--primary-700)",
    $border: "var(--primary-300)",
  },
  Professional: {
    $bg: "var(--primary-50)",
    $fg: "var(--primary-700)",
    $border: "var(--primary-200)",
  },
  "Professional Modifiers": {
    $bg: "var(--primary-100)",
    $fg: "var(--primary-800)",
    $border: "var(--primary-300)",
  },
  "Lesser-Of Charges": {
    $bg: "var(--primary-50)",
    $fg: "var(--primary-800)",
    $border: "var(--primary-200)",
  },
};

const StyledRuleTypeTag = styled(Tag)<{
  $bg: string;
  $fg: string;
  $border: string;
}>`
  &.p-tag.p-component {
    background: ${(p) => p.$bg} !important;
    color: ${(p) => p.$fg} !important;
    border: 1px solid ${(p) => p.$border} !important;
    font-weight: 600;
    font-size: 0.75rem;
    width: fit-content;
    border-radius: 8px;
  }
`;

const INPATIENT_TYPE_LABELS: Record<string, string> = {
  FIXED_COST: "Fixed Cost",
  PASS_THROUGH: "Pass Through",
  EXCESSIVE_LOS: "Excessive LOS",
  TRANSFER: "Transfer",
  STOP_LOSS: "Stop Loss",
  CHARGE_OUTLIER: "Charge Outlier",
  INPATIENT_OUTLIER: "Inpatient Outlier Rules",
};

/** Labels aligned with `InpatientRulesetForm` IPO trigger options (+ legacy engine types). */
const INPATIENT_OUTLIER_TRIGGER_LABELS: Record<string, string> = {
  STANDARD_CHARGE_THRESHOLD: "Standard Charge Threshold",
  MODIFIED_CHARGE_THRESHOLD: "Modified Charge Threshold (cost-to-charge)",
  LOS_RANGE: "LOS Range",
  PER_CODE_CHARGE_THRESHOLD: "Per Code Charge Threshold",
  DISCHARGE_STATUS: "Discharge Status",
  ALWAYS: "Always",
};

const TRANSFER_LOS_FORMULA_LABELS: Record<
  ITransferConfig["losFormula"],
  string
> = {
  LOS_MINUS_1: "Per Diem × (LOS - 1)",
  LOS: "Per Diem × LOS",
  LOS_PLUS_1: "Per Diem × (LOS + 1)",
};

function formatUsdFromCents(cents: number): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

function isInpatientOutlierConfig(
  config: unknown
): config is IInpatientOutlierConfig {
  return (
    typeof config === "object" &&
    config !== null &&
    "trigger" in config &&
    "formula" in config &&
    "mode" in config
  );
}

type SelectedRuleset = {
  id: string;
  ruleType:
    | "MPPR"
    | "Case Rate"
    | "Package"
    | "Exploding Fee Schedule"
    | "Hierarchy"
    | "Inpatient"
    | "Professional"
    | "Professional Modifiers"
    | "Lesser-Of Charges";
  providerId?: string;
  payerId?: number;
  rules?: { id: string }[];
};

function PayerRulesTable() {
  const [selectedItems, setSelectedItems] = useState<SelectedRuleset[]>([]);
  const [showDefaultsOnly, setShowDefaultsOnly] = useState(false);
  const { setIsLoading } = useLoading();
  const [deleteDiscountRuleset] = useDeleteDiscountRulesetMutation();
  const [deleteCaseRateRuleset] = useDeleteCaseRateRulesetMutation();
  const [deletePackageRuleset] = useDeletePackageRulesetMutation();
  const [deleteExplodingFeeSchedule] = useDeleteExplodingFeeScheduleMutation();
  const [deleteContractHierarchyRule] =
    useDeleteContractHierarchyRuleMutation();
  const [deleteInpatientRuleset] = useDeleteInpatientRulesetMutation();
  const [deleteProfessionalRuleset] = useDeleteProfessionalRulesetMutation();
  const [deleteModifierAdjustment] = useDeleteModifierAdjustmentMutation();
  const [deleteLesserOfCharges] = useDeleteLesserOfChargesMutation();

  const {
    data: discountRulesets,
    isLoading,
    refetch,
  } = useGetAllDiscountRulesetsQuery(
    showDefaultsOnly ? { isDefault: true } : undefined
  );
  const {
    data: caseRateRulesets,
    isLoading: isCaseRateLoading,
    refetch: refetchCaseRateRulesets,
  } = useGetCaseRateRulesetsQuery(
    showDefaultsOnly ? { isDefault: true } : undefined
  );
  const {
    data: packageRulesets,
    isLoading: isPackageLoading,
    refetch: refetchPackageRulesets,
  } = useGetAllPackageRulesetsQuery(
    showDefaultsOnly ? { isDefault: true } : undefined
  );
  const {
    data: explodingFeeSchedules,
    isLoading: isExplodingFeeSchedulesLoading,
    refetch: refetchExplodingFeeSchedules,
  } = useGetAllExplodingFeeSchedulesQuery(
    showDefaultsOnly ? { isDefault: true } : undefined
  );
  const {
    data: hierarchyRules,
    isLoading: isHierarchyRulesLoading,
    refetch: refetchHierarchyRules,
  } = useGetAllContractHierarchyRulesQuery();
  const {
    data: inpatientRulesets,
    isLoading: isInpatientRulesetsLoading,
    refetch: refetchInpatientRulesets,
  } = useGetInpatientRulesetsQuery(
    showDefaultsOnly ? { isDefault: true } : undefined
  );
  const {
    data: professionalRulesets,
    isLoading: isProfessionalRulesetsLoading,
    refetch: refetchProfessionalRulesets,
  } = useGetAllProfessionalRulesetsQuery();
  const {
    data: modifierAdjustments,
    isLoading: isModifierAdjustmentsLoading,
    refetch: refetchModifierAdjustments,
  } = useGetAllModifierAdjustmentsQuery(
    showDefaultsOnly ? { isDefault: true } : undefined
  );
  const {
    data: lesserOfCharges,
    isLoading: isLesserOfChargesLoading,
    refetch: refetchLesserOfCharges,
  } = useGetAllLesserOfChargesQuery(
    showDefaultsOnly ? { isDefault: true } : undefined
  );

  const { current: toastElement } = useContext(ToastContext);
  const navigate = useNavigate();

  const parsedDiscountRulesets = useMemo(() => {
    if (!discountRulesets?.data) return [];
    return discountRulesets.data.map((aDiscountRuleset) => {
      return {
        ...aDiscountRuleset,
        ruleType: "MPPR",
        ruleName: aDiscountRuleset.groupName ?? "-",
        providerName: aDiscountRuleset.provider?.name ?? "—",
        payerName: aDiscountRuleset?.payer?.name ?? "—",
        allowOnDuplicateCptCodes: aDiscountRuleset.allowOnDuplicateCptCodes
          ? "Enabled"
          : "Disabled",
      };
    });
  }, [discountRulesets]);

  const parsedCaseRateRulesets = useMemo(() => {
    if (!caseRateRulesets?.data) return [];
    return caseRateRulesets.data.map((aCaseRateRuleset) => {
      return {
        ...aCaseRateRuleset,
        ruleType: "Case Rate",
        ruleName: aCaseRateRuleset.groupName ?? "-",
        providerName: aCaseRateRuleset.provider?.name ?? "—",
        payerName: aCaseRateRuleset?.payer?.name ?? "—",
        allowOnDuplicateCptCodes: "—",
      };
    });
  }, [caseRateRulesets]);

  const parsedPackageRulesets = useMemo(() => {
    if (!packageRulesets?.data) return [];
    return packageRulesets.data.map((aPackageRuleset) => {
      return {
        ...aPackageRuleset,
        ruleType: "Package",
        ruleName: aPackageRuleset.groupName ?? "-",
        providerName: aPackageRuleset.provider?.name ?? "—",
        payerName: aPackageRuleset?.payer?.name ?? "—",
        cptCodes: aPackageRuleset.triggerCptCodes ?? [],
        allowOnDuplicateCptCodes: "—",
      };
    });
  }, [packageRulesets]);

  const parsedExplodingFeeSchedules = useMemo(() => {
    if (!explodingFeeSchedules?.data) return [];
    return explodingFeeSchedules.data.map((aRuleset) => {
      const totalRate = aRuleset.totalRateInCents / 100;
      const cptCount = aRuleset.cptCodes?.length ?? 0;
      return {
        ...aRuleset,
        ruleType: "Exploding Fee Schedule",
        ruleName: aRuleset.groupName ?? "-",
        providerName: aRuleset.provider?.name ?? "—",
        payerName: aRuleset.payer?.name ?? "—",
        allowOnDuplicateCptCodes: "—",
        totalRateFormatted: `$${totalRate.toFixed(2)}`,
        perCodeRateFormatted: `$${(cptCount > 0
          ? totalRate / cptCount
          : 0
        ).toFixed(2)}`,
      };
    });
  }, [explodingFeeSchedules]);

  const parsedHierarchyRules = useMemo(() => {
    if (!hierarchyRules?.data) return [];

    const groups = new Map<string, any[]>();

    hierarchyRules.data.forEach((rule) => {
      const key = `${rule.providerId}__${rule.payerId}`;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)?.push(rule);
    });

    return Array.from(groups.entries()).map(([key, rules]) => {
      const first = rules[0];
      return {
        id: key,
        key,
        ruleType: "Hierarchy" as const,
        providerId: first.providerId,
        payerId: first.payerId,
        providerName: first.provider?.name ?? "—",
        payerName: first.payer?.name ?? "—",
        ruleName: rules.map((r) => r.name).join(", "),
        groupName: rules.map((r) => r.name).join(", "),
        rules,
        ruleCount: rules.length,
        ruleSummary: rules.map((r) => r.name).join(", "),
        cptCodes: [],
        allowOnDuplicateCptCodes: "—",
      };
    });
  }, [hierarchyRules]);

  const parsedInpatientRulesets = useMemo(() => {
    if (!inpatientRulesets?.data) return [];
    return inpatientRulesets.data.map((ruleset) => ({
      ...ruleset,
      ruleType: "Inpatient" as const,
      ruleName: ruleset.name ?? "-",
      providerName: ruleset.provider?.name ?? "—",
      payerName: ruleset.payer?.name ?? "—",
      inpatientTypeLabel: INPATIENT_TYPE_LABELS[ruleset.type] || ruleset.type,
      cptCodes: ruleset.drgCodes ?? [],
      allowOnDuplicateCptCodes: "—",
    }));
  }, [inpatientRulesets]);

  const parsedProfessionalRulesets = useMemo(() => {
    if (!professionalRulesets?.data) return [];
    return professionalRulesets.data.map((ruleset) => ({
      ...ruleset,
      ruleType: "Professional" as const,
      ruleName: "—",
      providerName: ruleset.provider?.name ?? "—",
      payerName: ruleset.payer?.name ?? "—",
      cptCodes: [],
      allowOnDuplicateCptCodes: "—",
    }));
  }, [professionalRulesets]);

  const parsedModifierAdjustments = useMemo(() => {
    if (!modifierAdjustments?.data) return [];
    return modifierAdjustments.data.map((ruleset) => ({
      ...ruleset,
      ruleType: "Professional Modifiers" as const,
      ruleName: ruleset.groupName ?? `Modifier ${ruleset.modifier}`,
      providerName: ruleset.isDefault ? "—" : ruleset.provider?.name ?? "—",
      // A non-default modifier rule with no payer applies to every payer for this provider.
      payerName: ruleset.isDefault ? "—" : ruleset.payer?.name ?? "All payers",
      cptCodes: ruleset.cptCodes ?? [],
      allowOnDuplicateCptCodes: "—",
    }));
  }, [modifierAdjustments]);

  const parsedLesserOfCharges = useMemo(() => {
    if (!lesserOfCharges?.data) return [];
    return lesserOfCharges.data.map((ruleset) => ({
      ...ruleset,
      ruleType: "Lesser-Of Charges" as const,
      ruleName:
        ruleset.groupName ??
        (ruleset.scope === "LINE_CHARGES"
          ? "Lesser of (line)"
          : "Lesser of (total)"),
      providerName: ruleset.isDefault ? "—" : ruleset.provider?.name ?? "—",
      payerName: ruleset.isDefault ? "—" : ruleset.payer?.name ?? "All payers",
      // Backend unified cptCodes + drgCodes into a single `codes` column; render that
      // here under the shared "CPT/DRG Codes" column the table already has.
      cptCodes: ruleset.codes ?? [],
      allowOnDuplicateCptCodes: "—",
    }));
  }, [lesserOfCharges]);

  const parsedRulesets = useMemo(() => {
    const combined = [
      ...parsedDiscountRulesets,
      ...parsedCaseRateRulesets,
      ...parsedPackageRulesets,
      ...parsedExplodingFeeSchedules,
      // Hierarchy API has no default filter; contract hierarchy is not a default ruleset list.
      ...(showDefaultsOnly ? [] : parsedHierarchyRules),
      ...parsedInpatientRulesets,
      // Professional rulesets do not support defaults — exclude when showDefaultsOnly is on.
      ...(showDefaultsOnly ? [] : parsedProfessionalRulesets),
      // Modifier rules can be defaults; the query already filters by isDefault when needed.
      ...parsedModifierAdjustments,
      // Lesser-of-charges rules can be defaults; the query handles the filter.
      ...parsedLesserOfCharges,
    ];
    return combined.map((row) => ({
      ...row,
      cptCodesSearchText: Array.isArray(row.cptCodes)
        ? row.cptCodes.join(", ")
        : "",
    }));
  }, [
    showDefaultsOnly,
    parsedDiscountRulesets,
    parsedCaseRateRulesets,
    parsedPackageRulesets,
    parsedExplodingFeeSchedules,
    parsedHierarchyRules,
    parsedInpatientRulesets,
    parsedProfessionalRulesets,
    parsedModifierAdjustments,
    parsedLesserOfCharges,
  ]);

  function payerRuleAction(
    row: {
      id: string;
      ruleType: string;
      providerId?: string;
      payerId?: number;
    },
    action: "copy" | "edit" | "viewEdit"
  ) {
    const routePrefixByType: Record<string, string> = {
      "Case Rate": "case-rate",
      Package: "packages",
      "Exploding Fee Schedule": "exploding-fee-schedules",
      Inpatient: "inpatient-rulesets",
      MPPR: "mppr",
      Professional: "professional-rulesets",
      "Professional Modifiers": "professional-modifiers",
      "Lesser-Of Charges": "lesser-of-charges",
    };
    const routePrefix = routePrefixByType[row.ruleType];
    const hierarchyEditPath = `/payer-rules/hierarchy-rules/ruleset/${row.providerId}/${row.payerId}`;
    const actionPath = action === "copy" ? "copy" : "edit";
    const targetPath =
      row.ruleType === "Hierarchy"
        ? hierarchyEditPath
        : `/payer-rules/${routePrefix ?? "mppr"}/${actionPath}/${row.id}`;
    const actionLabel = action === "copy" ? "Copy" : "Edit";

    return (
      <Row>
        <Button className="p-button-link" onClick={() => navigate(targetPath)}>
          <p className="small-text">{actionLabel}</p>
        </Button>
      </Row>
    );
  }

  const cptCodesTransformer = (cptCodes: string[]) => {
    const allCodes = cptCodes.join(", ");
    return <ColumnWrapper>{allCodes}</ColumnWrapper>;
  };

  const ruleTypeTemplate = (row: {
    ruleType: string;
    inpatientTypeLabel?: string;
  }) => {
    const palette = RULE_TYPE_TAG_PALETTE[row.ruleType] ?? {
      $bg: "var(--primary-50)",
      $fg: "var(--primary-800)",
      $border: "var(--primary-300)",
    };
    return (
      <RuleTypeCell>
        <StyledRuleTypeTag
          value={row.ruleType}
          $bg={palette.$bg}
          $fg={palette.$fg}
          $border={palette.$border}
        />
        {row.ruleType === "Inpatient" && row.inpatientTypeLabel ? (
          <span className="text-sm text-gray-500">
            {row.inpatientTypeLabel}
          </span>
        ) : null}
      </RuleTypeCell>
    );
  };

  const additionalInfoTemplate = (row: {
    ruleType: string;
    type?: InpatientRulesetType;
    config?: unknown;
    discountPercentForRank2?: number;
    discountPercentForRank3?: number;
    discountPercentForRank4?: number;
    discountPercentForRank5Plus?: number;
    isPerDiem?: boolean;
    isBilateral?: boolean;
    bundledCptCodes?: string[];
    totalRateFormatted?: string;
    perCodeRateFormatted?: string;
    ruleCount?: number;
    ruleSummary?: string;
    allowOnDuplicateCptCodes?: string;
    compoundBilateralAndMppr?: boolean;
    endoApplies?: boolean;
    modifier?: string;
    multiplier?: number;
    scope?: string;
  }) => {
    if (row.ruleType === "Professional Modifiers") {
      const m = Number(row.multiplier ?? 1);
      const multiplierLabel = `×${m.toFixed(4).replace(/\.?0+$/, "")}`;
      return (
        <AdditionalInfoWrapper>
          <span>{`Modifier: ${row.modifier ?? "—"}`}</span>
          <span>{`Multiplier: ${multiplierLabel}`}</span>
        </AdditionalInfoWrapper>
      );
    }

    if (row.ruleType === "Lesser-Of Charges") {
      const m = Number(row.multiplier ?? 1);
      const multiplierLabel = `×${m.toFixed(4).replace(/\.?0+$/, "")}`;
      const scopeLabel =
        row.scope === "TOTAL_CHARGES" ? "Total charges" : "Line charges";
      return (
        <AdditionalInfoWrapper>
          <span>{`Scope: ${scopeLabel}`}</span>
          <span>{`Multiplier: ${multiplierLabel}`}</span>
        </AdditionalInfoWrapper>
      );
    }

    if (row.ruleType === "MPPR") {
      return (
        <AdditionalInfoWrapper>
          <span>{`${row.discountPercentForRank2 ?? 0}% | ${
            row.discountPercentForRank3 ?? 0
          }% | ${row.discountPercentForRank4 ?? 0}% | ${
            row.discountPercentForRank5Plus ?? 0
          }%`}</span>
          <span className="text-sm text-gray-600">{`Duplicates: ${
            row.allowOnDuplicateCptCodes === "Enabled" ? "ON" : "OFF"
          }`}</span>
        </AdditionalInfoWrapper>
      );
    }

    if (row.ruleType === "Case Rate") {
      return (
        <AdditionalInfoWrapper>
          <span>{`Per Diem: ${row.isPerDiem ? "Yes" : "No"}`}</span>
          <span>{`Bilateral: ${row.isBilateral ? "Yes" : "No"}`}</span>
        </AdditionalInfoWrapper>
      );
    }

    if (row.ruleType === "Package") {
      const bundledCodes = row.bundledCptCodes?.join(", ");
      const bundledText = `Bundled: ${bundledCodes || "-"}`;
      return <ColumnWrapper title={bundledText}>{bundledText}</ColumnWrapper>;
    }

    if (row.ruleType === "Exploding Fee Schedule") {
      return (
        <AdditionalInfoWrapper>
          <span>{`Total Rate: ${row.totalRateFormatted ?? "$0.00"}`}</span>
          <span>{`Per-Code Rate: ${row.perCodeRateFormatted ?? "$0.00"}`}</span>
        </AdditionalInfoWrapper>
      );
    }

    if (row.ruleType === "Hierarchy") {
      return (
        <AdditionalInfoWrapper>
          <span>{`Rules Count: ${row.ruleCount ?? 0}`}</span>
        </AdditionalInfoWrapper>
      );
    }

    if (row.ruleType === "Professional") {
      return (
        <AdditionalInfoWrapper>
          <span>{`Compound Bilateral + MPPR: ${
            row.compoundBilateralAndMppr ? "ON" : "OFF"
          }`}</span>
          <span>{`Endoscopy Rules: ${row.endoApplies ? "ON" : "OFF"}`}</span>
        </AdditionalInfoWrapper>
      );
    }

    if (row.ruleType === "Inpatient" && row.type && row.config != null) {
      if (
        row.type === "INPATIENT_OUTLIER" &&
        isInpatientOutlierConfig(row.config)
      ) {
        const hydrated = hydrateInpatientOutlierFormData(row.config);
        const triggerKey = hydrated.ipoTriggerType;
        const triggerLabel =
          (triggerKey && INPATIENT_OUTLIER_TRIGGER_LABELS[triggerKey]) ||
          triggerKey ||
          "—";
        return (
          <AdditionalInfoWrapper>
            <span>{`Trigger type: ${triggerLabel}`}</span>
          </AdditionalInfoWrapper>
        );
      }

      if (row.type === "PASS_THROUGH") {
        const cfg = row.config as IPassThroughConfig;
        const threshold =
          typeof cfg.thresholdInCents === "number"
            ? formatUsdFromCents(cfg.thresholdInCents)
            : "—";
        const mult =
          typeof cfg.chargeMultiplier === "number"
            ? String(cfg.chargeMultiplier)
            : "—";
        return (
          <AdditionalInfoWrapper>
            <span>{`Charge Threshold: ${threshold}`}</span>
            <span>{`Charge Multiplier: ${mult}`}</span>
          </AdditionalInfoWrapper>
        );
      }

      if (row.type === "TRANSFER") {
        const cfg = row.config as ITransferConfig;
        const losLabel =
          (cfg.losFormula && TRANSFER_LOS_FORMULA_LABELS[cfg.losFormula]) ||
          cfg.losFormula ||
          "—";
        return (
          <AdditionalInfoWrapper>
            <span>{`LOS Formula: ${losLabel}`}</span>
          </AdditionalInfoWrapper>
        );
      }
    }

    return "—";
  };

  const handleDeletePayerRules = async () => {
    setIsLoading(true);
    try {
      const selectedMpprIds = selectedItems
        .filter((item) => item.ruleType === "MPPR")
        .map((item) => item.id);
      const selectedCaseRateIds = selectedItems
        .filter((item) => item.ruleType === "Case Rate")
        .map((item) => item.id);
      const selectedPackageIds = selectedItems
        .filter((item) => item.ruleType === "Package")
        .map((item) => item.id);
      const selectedExplodingFeeScheduleIds = selectedItems
        .filter((item) => item.ruleType === "Exploding Fee Schedule")
        .map((item) => item.id);
      const selectedHierarchyRuleIds = selectedItems
        .filter((item) => item.ruleType === "Hierarchy")
        .flatMap((item) => item.rules?.map((rule) => rule.id) ?? []);
      const selectedInpatientIds = selectedItems
        .filter((item) => item.ruleType === "Inpatient")
        .map((item) => item.id);
      const selectedProfessionalIds = selectedItems
        .filter((item) => item.ruleType === "Professional")
        .map((item) => item.id);
      const selectedModifierAdjustmentIds = selectedItems
        .filter((item) => item.ruleType === "Professional Modifiers")
        .map((item) => item.id);
      const selectedLesserOfChargesIds = selectedItems
        .filter((item) => item.ruleType === "Lesser-Of Charges")
        .map((item) => item.id);

      await selectedMpprIds.reduce(
        (promiseChain, id) =>
          promiseChain.then(() => deleteDiscountRuleset(id).unwrap()),
        Promise.resolve()
      );

      await selectedCaseRateIds.reduce(
        (promiseChain, id) =>
          promiseChain.then(() => deleteCaseRateRuleset(id).unwrap()),
        Promise.resolve()
      );
      await selectedPackageIds.reduce(
        (promiseChain, id) =>
          promiseChain.then(() => deletePackageRuleset(id).unwrap()),
        Promise.resolve()
      );
      await selectedExplodingFeeScheduleIds.reduce(
        (promiseChain, id) =>
          promiseChain.then(() => deleteExplodingFeeSchedule(id).unwrap()),
        Promise.resolve()
      );
      await selectedHierarchyRuleIds.reduce(
        (promiseChain, id) =>
          promiseChain.then(() => deleteContractHierarchyRule(id).unwrap()),
        Promise.resolve()
      );
      await selectedInpatientIds.reduce(
        (promiseChain, id) =>
          promiseChain.then(() => deleteInpatientRuleset(id).unwrap()),
        Promise.resolve()
      );
      await selectedProfessionalIds.reduce(
        (promiseChain, id) =>
          promiseChain.then(() => deleteProfessionalRuleset(id).unwrap()),
        Promise.resolve()
      );
      await selectedModifierAdjustmentIds.reduce(
        (promiseChain, id) =>
          promiseChain.then(() => deleteModifierAdjustment(id).unwrap()),
        Promise.resolve()
      );
      await selectedLesserOfChargesIds.reduce(
        (promiseChain, id) =>
          promiseChain.then(() => deleteLesserOfCharges(id).unwrap()),
        Promise.resolve()
      );

      await refetch();
      await refetchCaseRateRulesets();
      await refetchPackageRulesets();
      await refetchExplodingFeeSchedules();
      await refetchHierarchyRules();
      await refetchInpatientRulesets();
      await refetchProfessionalRulesets();
      await refetchModifierAdjustments();
      await refetchLesserOfCharges();
      toastElement?.show({
        severity: "success",
        summary: "Deleted!",
        detail: "Payer Rules(s) has been deleted successfully.",
      });
    } catch (error) {
      await refetch();
      await refetchCaseRateRulesets();
      await refetchPackageRulesets();
      await refetchExplodingFeeSchedules();
      await refetchHierarchyRules();
      await refetchInpatientRulesets();
      await refetchProfessionalRulesets();
      await refetchModifierAdjustments();
      await refetchLesserOfCharges();
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "Error while deleting payer rules",
      });
    } finally {
      setIsLoading(false);
      setSelectedItems([]);
    }
  };

  const ruleTypeFilterTemplate = (options: any) => {
    const filterOptions = [
      "MPPR",
      "Case Rate",
      "Package",
      "Exploding Fee Schedule",
      "Hierarchy",
      "Inpatient",
      "Professional",
      "Professional Modifiers",
      "Lesser-Of Charges",
    ];
    return (
      <Dropdown
        value={options.value}
        options={filterOptions}
        onChange={(e) => options.filterApplyCallback(e.value)}
        placeholder="Select One"
        className="p-column-filter"
        showClear
        style={{ minWidth: "11rem", maxWidth: "11rem" }}
      />
    );
  };

  const textFilterTemplate = (options: any) => (
    <InputText
      value={options.value ?? ""}
      onChange={(e) => options.filterApplyCallback(e.target.value)}
      placeholder="Search"
      className="p-column-filter"
      style={{ minWidth: "200px", maxWidth: "200px" }}
    />
  );

  if (
    isLoading ||
    isCaseRateLoading ||
    isPackageLoading ||
    isExplodingFeeSchedulesLoading ||
    isHierarchyRulesLoading ||
    isInpatientRulesetsLoading ||
    isProfessionalRulesetsLoading ||
    isModifierAdjustmentsLoading ||
    isLesserOfChargesLoading
  ) {
    return <LoadingSpinner />;
  }
  return (
    <>
      <ToolbarRow>
        <span style={{ fontSize: 13 }}>Show defaults only</span>
        <InputSwitch
          checked={showDefaultsOnly}
          onChange={(e) => setShowDefaultsOnly(!!e.value)}
        />
      </ToolbarRow>
      <StyledDataTable
        rows={10}
        paginator
        stripedRows
        className="p-fluid"
        filterDisplay="row"
        value={parsedRulesets}
        responsiveLayout="scroll"
        style={{ cursor: "pointer" }}
        selection={selectedItems}
        emptyMessage="No payer rules to show."
        selectionMode="single"
        onSelectionChange={(event) => setSelectedItems(event.value)}
      >
        <Column selectionMode="multiple" headerStyle={{ width: "3em" }} />
        <Column
          filter
          field="ruleType"
          filterMatchMode="equals"
          style={{ minWidth: "140px" }}
          header="Rule Type"
          filterElement={ruleTypeFilterTemplate}
          showFilterMenu={false}
          body={ruleTypeTemplate}
        />
        <Column
          filter
          field="providerName"
          filterMatchMode="contains"
          style={{ minWidth: "230px", maxWidth: "230px" }}
          header="Provider Name"
          filterElement={textFilterTemplate}
          showFilterMenu={false}
          body={(row) =>
            defaultScopeCell(
              row,
              row.providerName,
              row.ruleType === "Professional Modifiers" ||
                row.ruleType === "Lesser-Of Charges"
                ? "Global"
                : undefined
            )
          }
        />
        <Column
          filter
          field="payerName"
          filterMatchMode="contains"
          style={{ minWidth: "230px", maxWidth: "230px" }}
          header="Payer Name"
          filterElement={textFilterTemplate}
          showFilterMenu={false}
          body={(row) =>
            defaultScopeCell(
              row,
              row.payerName,
              row.ruleType === "Professional Modifiers" ||
                row.ruleType === "Lesser-Of Charges"
                ? "Global"
                : undefined
            )
          }
        />
        <Column
          filter
          field="ruleName"
          filterMatchMode="contains"
          style={{ minWidth: "230px", maxWidth: "230px" }}
          header="Rule Name"
          filterElement={textFilterTemplate}
          showFilterMenu={false}
        />
        <Column
          filter
          field="cptCodesSearchText"
          filterMatchMode="contains"
          body={(row) => cptCodesTransformer(row.cptCodes)}
          style={{ minWidth: "230px", maxWidth: "230px" }}
          header="CPT/DRG Codes"
          filterElement={textFilterTemplate}
          showFilterMenu={false}
        />
        <Column
          field="additionalInfo"
          style={{ minWidth: "230px", maxWidth: "230px" }}
          header="Additional Info"
          body={additionalInfoTemplate}
        />
        <Column
          style={{ maxWidth: "100px" }}
          field="viewCopyPayerRule"
          body={(row) => {
            if (row.ruleType === "Hierarchy")
              return payerRuleAction(row, "viewEdit");
            // Professional rulesets are unique per (provider, payer) so copy doesn't apply.
            if (row.ruleType === "Professional") return null;
            // Modifier rules are edited directly; no copy flow yet.
            if (row.ruleType === "Professional Modifiers") return null;
            // Lesser-Of rules use the default → contract copy flow, not list copy.
            if (row.ruleType === "Lesser-Of Charges") return null;
            return payerRuleAction(row, "copy");
          }}
        />
        <Column
          style={{ maxWidth: "100px" }}
          field="viewEditPayerRule"
          body={(row) =>
            row.ruleType === "Hierarchy" ? null : payerRuleAction(row, "edit")
          }
        />
      </StyledDataTable>
      <div className="flex justify-content-end">
        <Button
          label="Delete"
          className="p-button-outlined mt-4"
          disabled={!selectedItems.length}
          onClick={() => {
            confirmDialog({
              header: "Confirmation",
              icon: "pi pi-exclamation-triangle",
              message: `Are you sure you want delete selected payer rules?`,
              accept: handleDeletePayerRules,
            });
          }}
        />
      </div>
    </>
  );
}

export default PayerRulesTable;
